import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { UUID } from 'angular2-uuid';
import { plainToInstance } from 'class-transformer';
import { SendMailSettingsControl } from '../../../components/common/sendmailsettings/send.mail.settings.control';
import { ChangeKeyDialog } from '../../../components/dialogs/changekeydialog/change.key.dialog';
import { MessageBoxHelper } from '../../../components/dialogs/messagebox/messagebox.dialog';
import { SettingsEntry } from '../../../components/pages/settings.page';
import { ArrayHelpers, TranslateFormatText } from '../../../helpers/array.helpers';
import { InjectorHelper } from '../../../helpers/injector.helper';
import { ForgotPWLayoutHelper } from '../../../helpers/login/forgot.pw.layout.helper';
import { LoginLayoutHelper } from '../../../helpers/login/login.layout.helper';
import { ResetPWLayoutHelper } from '../../../helpers/login/reset.pw.layout.helper';
import { NavigationHelper } from '../../../helpers/navigation.helper';
import { NotificationHelper } from '../../../helpers/notification.helper';
import { ThemeHelper } from '../../../helpers/theme.helpers';
import { MessageBoxButtons } from '../../../models/enums/messageboxbuttons.enum';
import { MessageBoxIcon } from '../../../models/enums/messageboxicon.enum';
import { MessageBoxResult } from '../../../models/enums/messageboxresult.enum';
import { Layout } from '../../../models/layout.model';
import { Theme } from '../../../models/style/theme.model';
import { ConfigService } from '../../../services/config.service';
import { GeneralSettingsService } from '../../../services/generalsettings.service';
import { JiraService } from '../../../services/jira.service';
import { LayoutService } from '../../../services/layout.service';
import { MetaService } from '../../../services/meta.service';
import { NavigationService } from '../../../services/navigation.service';
import { SideNavService } from '../../../services/sidenav.service';
import { UsersService } from '../../../services/users.service';
import { WorkflowService } from '../../../services/workflow.service';
import { DataCheck } from '../../../workflow/workflow.dialog';
import { VersioningOverview } from '../../versioningoverview/versioning.overview';
import { NavigationOverwiewSettings } from './navigation.overview.settings';

@Component({
    selector: 'navigation-overview-version-settings',
    templateUrl: './navigation.overview.version.settings.html'
})
export class NavigationOverviewVersionSettings implements OnInit, OnDestroy {
    ListItems = [];
    FilteredItems = [];
    selectedNode;
    HasChanges = false;
    SearchValue: string;
    SelectedItem;
    EditMode = false;
    ActiveUser;
    UserSub;

    Pages = [];
    Subscription;
    TempLayout;
    Themes;
    Workflows = [];
    Jiras;
    Projects;
    CSSFiles;
    FAVIcon;
    PWAIcon;

    private static _SettingsEntry: SettingsEntry = {
        Caption: '@@Navigation',
        ID: 'navigation',
        Icon: 'panel-left',
        Index: 20,
        Parent: 'layout',
        Security: {
            Name: 'evidanza.App.Shared.Security.LayoutRight',
            Value: 4
        },
        Children: [],
        UpdateFunction: NavigationOverviewVersionSettings.UpdateNodes
    };

    static GetSettingsEntry() {
        return NavigationOverviewVersionSettings._SettingsEntry;
    }

    private static UpdateNodes() {
        return new Promise<void>(resolve => {
            NavigationOverviewVersionSettings._SettingsEntry.Children = [];
            const ds = InjectorHelper.InjectorInstance.get<MetaService>(MetaService);
            ds.GetNavigationStructures().subscribe(nsList => {
                let index = 0;
                const list = [];
                list.push({
                    Caption: '@@Uebersicht',
                    ID: 'navi-overview',
                    Icon: 'panels-top-left',
                    Security: {
                        Name: 'evidanza.App.Shared.Security.LayoutRight',
                        Value: 4
                    },
                    Index: index++,
                    Content: NavigationOverviewVersionSettings
                });
                if (nsList) {
                    const gss = InjectorHelper.InjectorInstance.get<GeneralSettingsService>(GeneralSettingsService);
                    gss.GetDefaultNavigationStructure().then(defaultNS => {
                        nsList.forEach((ns: any) => {
                            const node = NavigationOverwiewSettings.GetNode(ns, index++);
                            if (defaultNS) {
                                if (ns.SID == defaultNS) {
                                    node.Caption += ' *';
                                }
                            } else if (ns.Caption == 'default') {
                                node.Caption += ' *';
                            }
                            list.push(node);
                        });
                        NavigationOverviewVersionSettings._SettingsEntry.Children = list;
                        resolve();
                    });
                } else {
                    NavigationOverviewVersionSettings._SettingsEntry.Children = list;
                    resolve();
                }
            });
        });
    }

    constructor(private cdRef: ChangeDetectorRef, private service: MetaService, private translate: TranslateService,
        private standardService: ConfigService, private sanitizer: DomSanitizer, private router: Router,
        private wfService: WorkflowService, private jira: JiraService) {
    }

    ngOnInit(): void {
        NavigationService.SelectedPage.next('layout');
        this.refresh();
        this.UserSub = UsersService.ActiveUser.subscribe(x => {
            this.ActiveUser = x;
        });
        this.standardService.GetManagementStatus().subscribe((result) => {
            if (result) {
                this.wfService.GetAllServiceWorkflows().subscribe(data => {
                    this.Workflows = data;
                });
            }
        });
        this.service.GetAllPages().subscribe(r => {
            if (r) {
                this.Pages.push(...r);
            }
        });
        this.service.GetCustomCSS(null).subscribe((files) => {
            this.CSSFiles = files;
        });
        this.service.GetThemes(null).subscribe(themes => {
            this.Themes = themes;
        });
        this.jira.GetAll().subscribe(jiras => {
            this.Jiras = jiras;
        });
        this.Subscription = LayoutService.LayoutEditMode.subscribe(x => {
            if (!x && this.TempLayout) {
                ThemeHelper.SetTheme(null);
                LayoutService.BaseLayoutEditMode.next(false);
                const sel = this.SelectedItem;
                if (sel) {
                    const layout = sel[this.TempLayout.Key];
                    if (layout) {
                        const actLayout = JSON.stringify(layout);
                        if (actLayout !== this.TempLayout.Value) {
                            this.setHasChanges(true);
                        }
                    }
                }
                this.TempLayout = null;
            }
        });
    }

    ngOnDestroy(): void {
        NavigationService.SelectedPage.next('settings');
        if (this.UserSub) {
            this.UserSub.unsubscribe();
            this.UserSub = null;
        }
        if (this.Subscription) {
            this.Subscription.unsubscribe();
            this.Subscription = null;
        }
    }

    UpdateFiltered() {
        let list = [];
        if (this.ListItems) {
            if (this.SearchValue) {
                const toLower = this.SearchValue.toLowerCase();
                list = this.ListItems.filter(x => x.Caption.toLowerCase().indexOf(toLower) > -1);
            } else {
                list.push(...this.ListItems);
            }
        }
        this.FilteredItems = list;
    }

    onItemClick(selection) {
        if (this.selectedNode !== selection) {
            this.checkHasChanges().then(check => {
                if (check) {
                    this.selectedNode = selection;
                    this.setSelectedItem(null);
                    if (selection && selection.SID) {
                        this.service.LoadNavigationStructure(selection.SID).subscribe((result) => {
                            if (result) {
                                this.setSelectedItem(result);
                                this.EditMode = this.ActiveUser && selection.CanEditInfo && selection.CanEditInfo.EditUserID == this.ActiveUser.SID;
                            }
                        });
                    }
                }
            });
        }
    }

    setSelectedItem(item) {
        this.EditMode = false;
        this.SelectedItem = item;
        this.PWAIcon = null;
        this.FAVIcon = null;
        if (item) {
            if (item.PWAIcon) {
                this.PWAIcon = this.sanitizer.bypassSecurityTrustResourceUrl(item.PWAIcon);
            }
            if (item.FAVIcon) {
                this.FAVIcon = this.sanitizer.bypassSecurityTrustResourceUrl(item.FAVIcon);
            }
            if (item.Jira) {
                var config = this.Jiras.find(x => x.SID == item.Jira);
                this.jira.GetAllProjects(config).subscribe(projects => {
                    this.Projects = projects;
                    this.cdRef.detectChanges();
                });
            }
            let hasChanges = false;
            if (item.BaseLayout && !item.BaseLayout._Id) {
                item.BaseLayout._Id = UUID.UUID();
                hasChanges = true;
            }
            if (item.LoginLayout && !item.LoginLayout._Id) {
                item.LoginLayout._Id = UUID.UUID();
                hasChanges = true;
            }
            if (!item.ForgotPasswordSettings) {
                item.ForgotPasswordSettings = {};
            }
            if (hasChanges) {
                this.setHasChanges(true);
            }
        }
        this.cdRef.detectChanges();
    }

    setHasChanges(hasChanges) {
        if (typeof hasChanges === 'boolean') {
            this.HasChanges = hasChanges;
            LayoutService.PreventBrowserClose.next(this.HasChanges);
        }
        this.cdRef.detectChanges();
    }

    checkHasChanges() {
        return new Promise<boolean>(resolve => {
            if (this.HasChanges) {
                if (this.selectedNode && this.selectedNode.CanEditInfo && !this.selectedNode.CanEditInfo.CanEdit) {
                    resolve(true);
                } else {
                    MessageBoxHelper.ShowDialog(new TranslateFormatText('@@Wollen Sie die Aenderungen speichern?'),
                        new TranslateFormatText('@@Frage'), MessageBoxButtons.YesNoAbort, MessageBoxIcon.Question).then(retVal => {
                            if (retVal === MessageBoxResult.Yes) {
                                if (this.EditMode) {
                                    this.save(false).then(x => {
                                        resolve(x);
                                    });
                                } else {
                                    this.setEditMode(false).then(x => {
                                        resolve(x);
                                    });
                                }
                            } else if (retVal === MessageBoxResult.Abort) {
                                resolve(false);
                            } else { // No
                                this.setHasChanges(false);
                                resolve(true);
                            }
                        });
                }
            } else {
                resolve(true);
            }
        });
    }

    setEditMode(fromButton) {
        return new Promise<boolean>(resolve => {
            if (this.SelectedItem) {
                this.service.RequestNavigationStructureLock(this.SelectedItem).subscribe(x => {
                    if (x && x.CanEditInfo) {
                        let index = -1;
                        if (this.ListItems.some((y, i) => {
                            if (x.SID == y.SID) {
                                index = i;
                                return true;
                            }
                            return false;
                        })) {
                            this.ListItems.splice(index, 1, x);
                            this.checkItemStyle(x);
                            if (fromButton) {
                                this.selectedNode = x;
                            }
                        }
                        if (x.CanEditInfo.CanEdit) {
                            this.EditMode = true;
                            this.setHasChanges(false);
                            const text = new TranslateFormatText('@@Navigation \'{0}\' erfolgreich gespeichert.');
                            text.FormatParams.push(x.Caption);
                            NotificationHelper.Success(text, new TranslateFormatText('@@Navigation speichern'));
                            this.cdRef.detectChanges();
                            resolve(true);
                        } else {
                            const text = new TranslateFormatText(x.CanEditInfo.NewVersion ? '@@AlreadySavedBy{0}' : '@@LockedBy{0}');
                            text.FormatParams.push(x.CanEditInfo.EditUserName);
                            MessageBoxHelper.ShowDialog(text, new TranslateFormatText('@@LockedObject'),
                                MessageBoxButtons.Ok, MessageBoxIcon.Information);
                            resolve(false);
                        }
                    } else {
                        resolve(true);
                    }
                });
            } else {
                resolve(true);
            }
        });
    }

    checkItemStyle(data) {
        if (data) {
            const style = {};
            if (data.IsCapsule) {
                style['font-style'] = 'italic';
            }
            if (data.IsOverridden) {
                style['font-weight'] = 'bold';
            }
            if (data.CanEditInfo) {
                if (data.CanEditInfo.CanEdit) {
                    if (this.ActiveUser && data.CanEditInfo.EditUserID == this.ActiveUser.SID) {
                        style['text-decoration'] = 'underline';
                    }
                } else {
                    style['text-decoration'] = 'line-through';
                }
            }
            data.Style = style;
        }
    }

    save(fromSaveButton: boolean): Promise<boolean> {
        return new Promise<boolean>(resolve => {
            LayoutService.Loading.next(true);
            this.checkCanSave().then(dc => {
                if (dc.IsCorrect) {
                    const sel = this.SelectedItem;
                    if (sel) {
                        this.service.SaveNavigationStructureToChangeLog(sel).subscribe((result) => {
                            if (result) {
                                this.setHasChanges(false);
                                const text = new TranslateFormatText('@@Navigation \'{0}\' erfolgreich gespeichert.');
                                text.FormatParams.push(result.Caption);
                                NotificationHelper.Success(text, new TranslateFormatText('@@Navigation speichern'));
                                let index = -1;
                                if (this.ListItems.some((x, i) => {
                                    if (x.SID == result.SID) {
                                        index = i;
                                        return true;
                                    }
                                    return false;
                                })) {
                                    this.ListItems.splice(index, 1, result);
                                } else { // neu
                                    sel._Id = result.SID;
                                    sel._version = result.Version;
                                    NavigationService.Refresh.next(NavigationOverviewVersionSettings._SettingsEntry.ID);
                                    this.ListItems.push(result);
                                    ArrayHelpers.sortAlphabetical(this.ListItems, 'Caption');
                                    this.UpdateFiltered();
                                    // neue Struktur wird in Meta gespeichert -> neuer CheckOut n�tig
                                    this.EditMode = this.ActiveUser && result.CanEditInfo && result.CanEditInfo.EditUserID == this.ActiveUser.SID;
                                }
                                this.checkItemStyle(result);
                                if (fromSaveButton) {
                                    this.selectedNode = result;
                                }
                            } else {
                                NotificationHelper.Error(new TranslateFormatText('@@AlreadySavedByAdmin'),
                                    new TranslateFormatText('@@Navigation speichern'));
                            }
                            LayoutService.Loading.next(false);
                        });
                    } else {
                        LayoutService.Loading.next(false);
                    }
                } else {
                    const t = '@@Aenderungen konnten nicht gespeichert werden. Bitte ueberpruefen Sie folgende Einstellungen:\n{0}';
                    const text = new TranslateFormatText(t);
                    text.FormatParams.push(dc.Error);
                    MessageBoxHelper.ShowDialog(text, new TranslateFormatText('@@Warnung'), MessageBoxButtons.Ok, MessageBoxIcon.Warning);
                    LayoutService.Loading.next(false);
                }
                resolve(dc.IsCorrect);
            });
        });
    }

    checkCanSave(): Promise<DataCheck> {
        return new Promise<DataCheck>(resolve => {
            const sel = this.SelectedItem;
            if (sel) {
                if (sel._key) {
                    this.service.GetNavigationStructures().subscribe(nsList => {
                        const toLower = sel._key.toLowerCase();
                        const retVal = new DataCheck();
                        if (nsList.some(x => x.Caption.toLowerCase() === toLower && x.SID !== sel._Id)) {
                            const text = '@@Es existiert bereits eine Navigation mit dem angegebenen Key. Bitte aendern Sie den Key.';
                            retVal.Error = this.translate.instant(text);
                            retVal.IsCorrect = false;
                        }
                        resolve(retVal);
                    });
                } else {
                    const retVal = new DataCheck();
                    retVal.Error = this.translate.instant('@@Bitte vergeben Sie einen Key');
                    retVal.IsCorrect = false;
                    resolve(retVal);
                }
            } else {
                resolve(new DataCheck());
            }
        });
    }

    refresh() {
        this.checkHasChanges().then(check => {
            if (check) {
                LayoutService.Loading.next(true);
                this.SearchValue = null;
                this.EditMode = false;
                this.ListItems = [];
                this.FilteredItems = [];
                this.selectedNode = null;
                this.setSelectedItem(null);
                this.HasChanges = false;
                this.service.LoadNavigationStructures().subscribe((result) => {
                    if (result) {
                        result.forEach(x => this.checkItemStyle(x));
                        ArrayHelpers.sortAlphabetical(result, 'Caption');
                        this.ListItems = result;
                        this.UpdateFiltered();
                    }
                    LayoutService.Loading.next(false);
                    this.cdRef.detectChanges();
                });
            }
        });
    }

    addItem() {
        this.checkHasChanges().then(check => {
            if (check) {
                this.selectedNode = null;
                this.setSelectedItem(null);
                this.setSelectedItem({});
                this.EditMode = true;
            }
        });
    }

    deleteItem() {
        const sel = this.selectedNode;
        if (sel && !this.EditMode) {
            if (sel.CanEditInfo && !sel.CanEditInfo.CanEdit) {
                return;
            }
            const tft = new TranslateFormatText('@@Sind Sie sicher, dass Sie die Navigation \'{0}\' loeschen moechten? ');
            tft.FormatParams.push(sel.Caption);
            MessageBoxHelper.ShowDialog(tft, new TranslateFormatText('@@Navigation loeschen'),
                MessageBoxButtons.YesNo, MessageBoxIcon.Question).then(x => {
                    if (x == MessageBoxResult.Yes) {
                        this.deleteItemInternal(sel, false);
                    }
                });
        }
    }

    private deleteItemInternal(sel, ignoreVersions: boolean) {
        this.service.TryDeleteNavigationStructure(sel.SID, ignoreVersions).subscribe(result => {
            if (result) {
                if (result.Deleted) {
                    this.setHasChanges(false);
                    this.refresh();
                    const success = new TranslateFormatText('@@Navigation \'{0}\' erfolgreich geloescht.');
                    success.FormatParams.push(sel.Caption);
                    NotificationHelper.Success(success, '@@Navigation loeschen');
                } else if (result.IsLocked) {
                    const text = new TranslateFormatText('@@LockedBy{0}');
                    text.FormatParams.push(result.LockedBy);
                    MessageBoxHelper.ShowDialog(text, new TranslateFormatText('@@LockedObject'),
                        MessageBoxButtons.Ok, MessageBoxIcon.Information);
                } else if (result.MultipleVersions) {
                    const text = new TranslateFormatText('@@MultipleVersionsInfoText');
                    text.FormatParams.push(result.LockedBy);
                    MessageBoxHelper.ShowDialog(text, new TranslateFormatText('@@MultipleVersionsInfoHeader'),
                        MessageBoxButtons.None, MessageBoxIcon.Information, 500, null, [
                        { Caption: '@@DeleteAllVersions', ID: 0 },
                        { Caption: '@@GoToVersionsOverview', ID: 1 },
                        { Caption: '@@Cancel', ID: 2 }
                    ]).then(result => {
                        if (result == 0) {
                            this.deleteItemInternal(sel, true);
                        } else if (result == 1) {
                            const navStruct = SideNavService.SelectedNavigationStructure.getValue();
                            if (navStruct) {
                                this.router.navigateByUrl(navStruct + VersioningOverview.Path);
                            }
                        }
                    });
                } else {
                    MessageBoxHelper.ShowDialog(new TranslateFormatText('@@CannotDeleteDefaultStructure'),
                        new TranslateFormatText('@@Information'), MessageBoxButtons.Ok, MessageBoxIcon.Information);
                }
            }
        });
    }

    OnJiraChanged(item) {
        this.setHasChanges(true);
        const config = this.Jiras.find(x => x.SID == item.value);
        if (config) {
            this.jira.GetAllProjects(config).subscribe(projects => {
                this.Projects = projects;
                this.cdRef.detectChanges();
            });
        }
    }

    checkTheme(themeID) {
        return new Promise<void>((resolve) => {
            if (themeID) {
                this.service.GetTheme(themeID).subscribe((theme) => {
                    if (theme) {
                        ThemeHelper.SetTheme(plainToInstance(Theme, theme));
                    }
                    resolve();
                });
            } else {
                resolve();
            }
        });
    }

    editLayout() {
        const sel = this.SelectedItem;
        if (sel) {
            this.checkTheme(sel.DefaultTheme).then(() => {
                if (sel.BaseLayout) {
                    if (!(sel.BaseLayout instanceof Layout)) {
                        sel.BaseLayout = plainToInstance(Layout, sel.BaseLayout);
                    }
                } else {
                    sel.BaseLayout = NavigationHelper.GetDefaultBaseLayout();
                    sel.BaseLayout._Id = UUID.UUID();
                }
                LayoutService.SelectedLayout.next(sel.BaseLayout);
                LayoutService.BaseLayoutEditMode.next(true);
                LayoutService.LayoutEditMode.next(true);
                this.TempLayout = {
                    Key: 'BaseLayout',
                    Value: JSON.stringify(sel.BaseLayout)
                };
            });
        }
    }

    resetBaseLayout() {
        if (this.SelectedItem) {
            this.SelectedItem.BaseLayout = null;
            this.setHasChanges(true);
        }
    }

    editLogin() {
        const sel = this.SelectedItem;
        if (sel) {
            this.checkTheme(sel.DefaultTheme).then(() => {
                if (sel.LoginLayout) {
                    if (!(sel.LoginLayout instanceof Layout)) {
                        sel.LoginLayout = plainToInstance(Layout, sel.LoginLayout);
                    }
                } else {
                    sel.LoginLayout = LoginLayoutHelper.GetLayout();
                    sel.LoginLayout._Id = UUID.UUID();
                }
                LayoutService.SelectedLayout.next(sel.LoginLayout);
                LayoutService.LayoutEditMode.next(true);
                this.TempLayout = {
                    Key: 'LoginLayout',
                    Value: JSON.stringify(sel.LoginLayout)
                };
            });
        }
    }


    resetLoginLayout() {
        if (this.SelectedItem) {
            this.SelectedItem.LoginLayout = null;
            this.setHasChanges(true);
        }
    }

    editForgotPWLayout() {
        const sel = this.SelectedItem;
        if (sel) {
            this.checkTheme(sel.DefaultTheme).then(() => {
                if (sel.ForgotPWLayout) {
                    if (!(sel.ForgotPWLayout instanceof Layout)) {
                        sel.ForgotPWLayout = plainToInstance(Layout, sel.ForgotPWLayout);
                    }
                } else {
                    sel.ForgotPWLayout = ForgotPWLayoutHelper.GetLayout();
                    sel.ForgotPWLayout._Id = UUID.UUID();
                }
                LayoutService.SelectedLayout.next(sel.ForgotPWLayout);
                LayoutService.LayoutEditMode.next(true);
                this.TempLayout = {
                    Key: 'ForgotPWLayout',
                    Value: JSON.stringify(sel.ForgotPWLayout)
                };
            });
        }
    }

    resetForgotPWLayout() {
        if (this.SelectedItem) {
            this.SelectedItem.ForgotPWLayout = null;
            this.setHasChanges(true);
        }
    }

    editForgotPWMail() {
        const sel = this.SelectedItem;
        if (sel) {
            SendMailSettingsControl.ShowDialog(sel.ForgotPWSettings, (result) => {
                if (result) {
                    if (result.Body && result.Body.indexOf('$$link$$') < 0) {
                        MessageBoxHelper.ShowDialog(new TranslateFormatText('@@MissingLink'),
                            new TranslateFormatText('@@Information'), MessageBoxButtons.Ok, MessageBoxIcon.Information);
                        return false;
                    }
                    sel.ForgotPWSettings = result;
                    this.setHasChanges(true);
                }
                return true;
            });
        }
    }

    resetForgotPWMail() {
        if (this.SelectedItem) {
            this.SelectedItem.ForgotPWSettings = null;
            this.setHasChanges(true);
        }
    }

    editResetPWLayout() {
        const sel = this.SelectedItem;
        if (sel) {
            this.checkTheme(sel.DefaultTheme).then(() => {
                if (sel.ResetPWLayout) {
                    if (!(sel.ResetPWLayout instanceof Layout)) {
                        sel.ResetPWLayout = plainToInstance(Layout, sel.ResetPWLayout);
                    }
                } else {
                    sel.ResetPWLayout = ResetPWLayoutHelper.GetLayout(); // TODO
                    sel.ResetPWLayout._Id = UUID.UUID();
                }
                LayoutService.SelectedLayout.next(sel.ResetPWLayout);
                LayoutService.LayoutEditMode.next(true);
                this.TempLayout = {
                    Key: 'ResetPWLayout',
                    Value: JSON.stringify(sel.ResetPWLayout)
                };
            });
        }
    }

    resetResetPWLayout() {
        if (this.SelectedItem) {
            this.SelectedItem.ResetPWLayout = null;
            this.setHasChanges(true);
        }
    }

    UploadFAV(ev) {
        if (ev && ev.target) {
            const selectedFile = ev.target.files[0];
            if (selectedFile) {
                const fr = new FileReader();
                const item = this.SelectedItem;
                const that = this;
                fr.addEventListener('load', function () {
                    item.FAVIcon = fr.result;
                    that.setHasChanges(true);
                }, false);
                fr.readAsDataURL(selectedFile);
            }
        }
    }

    UploadPWA(ev) {
        if (ev && ev.target) {
            const selectedFile = ev.target.files[0];
            if (selectedFile) {
                const fr = new FileReader();
                const item = this.SelectedItem;
                const that = this;
                fr.addEventListener('load', function () {
                    item.PWAIcon = fr.result;
                    that.setHasChanges(true);
                }, false);
                fr.readAsDataURL(selectedFile);
            }
        }
    }

    editKey() {
        const sel = this.SelectedItem;
        if (sel) {
            const forbiddenKeys = [...SideNavService.SystemURLs];
            this.ListItems.forEach(x => {
                forbiddenKeys.push(x.Caption);
            });
            ChangeKeyDialog.ShowDialog(forbiddenKeys, true, (key) => {
                if (key) {
                    this.service.ChangeNavigationStructureKey(sel._Id, key).subscribe(x => {
                        if (typeof x === 'number') {
                            if (x == 0) {
                                sel._key = key;
                                NotificationHelper.Success('@@SuccessfullyReplacedKeyText', '@@SuccessfullyReplacedKeyHeader');
                            } else {
                                ChangeKeyDialog.ShowMessage(x);
                            }
                        }
                    });
                }
            });
        }
    }
}
